import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, CheckCircle2, User, Mail, Phone, FileText } from "lucide-react";
import { api, StorageKeys } from "../lib/api";
import { storage, getDeviceId } from "../lib/storage";
import { useI18n } from "../lib/i18n";
import { cn } from "../lib/utils";

/* ═══════════════ 3-LANGUAGE COPY ═══════════════ */
const COPY = {
  en: {
    badge: "GurSetu",
    titles: ["Share Your Query", "Share Your Feedback", "Share Your Suggestion", "Share Your Issue"],
    subtitle: "Your voice matters to us",
    refLabel: "Your Reference ID",
    nameLabel: "Your Name",
    namePh: "e.g. Karan",
    contactLabel: "Email or Phone — any one",
    emailPh: "you@example.com",
    phonePh: "+91 98765 43210 (country code with)",
    or: "OR",
    msgLabel: "Your Query / Feedback",
    msgPh: "Write anything… a suggestion, a problem, an idea…",
    submit: "Send Message",
    sending: "Sending…",
    successTitle: "Message Sent!",
    successBody: "🙏 Thank you! GurSetu Team will read your message and contact you very soon.",
    successRef: "Screenshot & Save this ID to track your submission",
    sendAnother: "Send another message",
    errName: "Please enter your name",
    errContact: "Please provide either email or phone number",
    errEmail: "Please enter a valid email address",
    errPhone: "Please enter a valid phone number (with country code)",
    errMsg: "Please write your message (min 5 characters)",
    errSend: "Could not send. Please try again.",
  },
  hi: {
    badge: "गुरसेतु",
    titles: ["अपना सवाल साझा करें", "अपना फ़ीडबैक साझा करें", "अपना सुझाव साझा करें", "अपनी समस्या साझा करें"],
    subtitle: "संगत की आवाज़ हमारे लिए ज़रूरी है",
    refLabel: "आपकी रेफ़रेंस आईडी",
    nameLabel: "आपका नाम",
    namePh: "जैसे: करन",
    contactLabel: "ईमेल या फ़ोन — कोई एक",
    emailPh: "you@example.com",
    phonePh: "+91 98765 43210",
    or: "या",
    msgLabel: "आपका सवाल / फ़ीडबैक",
    msgPh: "कुछ भी लिखें… सुझाव, समस्या, विचार…",
    submit: "संदेश भेजें",
    sending: "भेज रहे हैं…",
    successTitle: "संदेश भेज दिया गया!",
    successBody: "🙏 धन्यवाद! गुरसेतु टीम जल्द आपका संदेश पढ़ेगी।",
    successRef: "स्क्रीनशॉट लेकर इस ID को सेव करें",
    sendAnother: "एक और संदेश भेजें",
    errName: "कृपया अपना नाम लिखें",
    errContact: "कृपया ईमेल या फ़ोन नंबर में से कोई एक दें",
    errEmail: "कृपया सही ईमेल पता लिखें",
    errPhone: "कृपया सही फ़ोन नंबर लिखें (7–15 अंक)",
    errMsg: "कृपया अपना संदेश लिखें (कम से कम 5 अक्षर)",
    errSend: "भेजा नहीं जा सका। फिर कोशिश करें।",
  },
  pa: {
    badge: "ਗੁਰਸੇਤੂ",
    titles: ["ਆਪਣਾ ਸਵਾਲ ਸਾਂਝਾ ਕਰੋ", "ਆਪਣਾ ਫ਼ੀਡਬੈਕ ਸਾਂਝਾ ਕਰੋ", "ਆਪਣਾ ਸੁਝਾਅ ਸਾਂਝਾ ਕਰੋ", "ਆਪਣੀ ਸਮੱਸਿਆ ਸਾਂਝੀ ਕਰੋ"],
    subtitle: "ਸੰਗਤ ਦੀ ਅਵਾਜ਼ ਸਾਡੇ ਲਈ ਜ਼ਰੂਰੀ ਹੈ",
    refLabel: "ਤੁਹਾਡੀ ਰੈਫਰੈਂਸ ID",
    nameLabel: "ਤੁਹਾਡਾ ਨਾਮ",
    namePh: "ਜਿਵੇਂ: ਕਰਨ",
    contactLabel: "ਈਮੇਲ ਜਾਂ ਫ਼ੋਨ — ਕੋਈ ਇੱਕ",
    emailPh: "you@example.com",
    phonePh: "+91 98765 43210",
    or: "ਜਾਂ",
    msgLabel: "ਤੁਹਾਡਾ ਸਵਾਲ / ਫ਼ੀਡਬੈਕ",
    msgPh: "ਕੁਝ ਵੀ ਲਿਖੋ… ਸੁਝਾਅ, ਸਮੱਸਿਆ, ਵਿਚਾਰ…",
    submit: "ਸੁਨੇਹਾ ਭੇਜੋ",
    sending: "ਭੇਜ ਰਹੇ ਹਾਂ…",
    successTitle: "ਸੁਨੇਹਾ ਭੇਜ ਦਿੱਤਾ!",
    successBody: "🙏 ਧੰਨਵਾਦ! ਗੁਰਸੇਤੂ ਟੀਮ ਜਲਦੀ ਤੁਹਾਡਾ ਸੁਨੇਹਾ ਪੜ੍ਹੇਗੀ।",
    successRef: "ਸਕ੍ਰੀਨਸ਼ੌਟ ਲੈ ਕੇ ਇਹ ID ਸੇਵ ਕਰੋ",
    sendAnother: "ਇੱਕ ਹੋਰ ਸੁਨੇਹਾ ਭੇਜੋ",
    errName: "ਕਿਰਪਾ ਕਰਕੇ ਆਪਣਾ ਨਾਮ ਲਿਖੋ",
    errContact: "ਕਿਰਪਾ ਕਰਕੇ ਈਮੇਲ ਜਾਂ ਫ਼ੋਨ ਨੰਬਰ ਵਿੱਚੋਂ ਕੋਈ ਇੱਕ ਦਿਓ",
    errEmail: "ਕਿਰਪਾ ਕਰਕੇ ਸਹੀ ਈਮੇਲ ਪਤਾ ਲਿਖੋ",
    errPhone: "ਕਿਰਪਾ ਕਰਕੇ ਸਹੀ ਫ਼ੋਨ ਨੰਬਰ ਲਿਖੋ (7–15 ਅੰਕ)",
    errMsg: "ਕਿਰਪਾ ਕਰਕੇ ਆਪਣਾ ਸੁਨੇਹਾ ਲਿਖੋ (ਘੱਟੋ-ਘੱਟ 5 ਅੱਖਰ)",
    errSend: "ਭੇਜਿਆ ਨਹੀਂ ਜਾ ਸਕਿਆ। ਫਿਰ ਕੋਸ਼ਿਸ਼ ਕਰੋ।",
  },
};

const LANGS = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिंदी" },
  { code: "pa", label: "ਪੰਜਾਬੀ" },
];

function makeRefId() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let s = "";
  for (let i = 0; i < 6; i++) s += chars[Math.floor(Math.random() * chars.length)];
  return `GS-${s}`;
}

const validEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim());
const validPhone = (v) => {
  const digits = v.replace(/[^\d]/g, "");
  return digits.length >= 7 && digits.length <= 15;
};

/* ═══════════════ ROTATING TITLE ═══════════════ */
function RotatingTitle({ titles }) {
  const [i, setI] = useState(0);

  useEffect(() => {
    setI(0);
    const iv = setInterval(() => setI((p) => (p + 1) % titles.length), 2400);
    return () => clearInterval(iv);
  }, [titles]);

  return (
    <span className="relative block h-7 sm:h-9 overflow-hidden">
      <AnimatePresence mode="wait">
        <motion.span
          key={`${i}-${titles[i]}`}
          initial={{ opacity: 0, y: 25, filter: "blur(7px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          exit={{ opacity: 0, y: -25, filter: "blur(7px)" }}
          transition={{ duration: 0.45, ease: "easeOut" }}
          className="block text-[22px] sm:text-[30px] font-extrabold leading-7 sm:leading-9 text-slate-900 dark:text-white"
        >
          {titles[i]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════
   ✅ FLOATING BUTTON HATA DIYA HAI —
   Ab feedback SIRF sidebar ("Feedback & Queries") se khulega
   (gursetu:open-feedback event se).
═══════════════════════════════════════════════════════════ */
export default function FeedbackWidget() {
  const { lang: appLang } = useI18n();
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState(["en", "hi", "pa"].includes(appLang) ? appLang : "en");
  const [refId, setRefId] = useState(makeRefId);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const t = COPY[lang] || COPY.en;

  /* Sidebar event se open hota hai */
  useEffect(() => {
    const openFromAnywhere = () => setOpen(true);
    window.addEventListener("gursetu:open-feedback", openFromAnywhere);
    return () => window.removeEventListener("gursetu:open-feedback", openFromAnywhere);
  }, []);

  /* Modal khulne par body scroll lock */
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const resetForm = () => {
    setRefId(makeRefId());
    setName(""); setEmail(""); setPhone(""); setMessage("");
    setError(""); setSent(false);
  };

  const submit = async () => {
    setError("");
    if (!name.trim()) return setError(t.errName);
    const hasEmail = email.trim().length > 0;
    const hasPhone = phone.trim().length > 0;
    if (!hasEmail && !hasPhone) return setError(t.errContact);
    if (hasEmail && !validEmail(email)) return setError(t.errEmail);
    if (hasPhone && !validPhone(phone)) return setError(t.errPhone);
    if (message.trim().length < 5) return setError(t.errMsg);

    setSending(true);
    try {
      await api.post("/feedback", {
        refId,
        name: name.trim(),
        email: hasEmail ? email.trim() : null,
        phone: hasPhone ? phone.trim() : null,
        message: message.trim(),
        language: lang,
        userId: storage.get(StorageKeys.UserId, "") || null,
        deviceId: getDeviceId(),
      });
      setSent(true);
    } catch (e) {
      setError(t.errSend);
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-amber-300/60 dark:border-amber-400/25 bg-white dark:bg-white/[0.05] px-4 py-3 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 outline-none focus:border-amber-500 dark:focus:border-amber-400 transition-colors";

  const labelCls = "block text-[10px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1";

  /* ❌ Koi floating button NAHI — sirf modal */
  return (
    typeof document !== "undefined" &&
    createPortal(
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[2147483647] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 40, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.9, y: 40, opacity: 0 }}
              transition={{ type: "spring", damping: 26, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-md max-h-[88vh] overflow-y-auto rounded-3xl bg-[#fffdf8] dark:bg-[#101722] border border-amber-400/40 shadow-2xl p-6"
            >
              {/* CLOSE */}
              <button
                onClick={() => setOpen(false)}
                className="absolute top-4 right-4 w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 dark:bg-white/10 dark:hover:bg-white/20 dark:text-white grid place-items-center z-10"
              >
                <X size={17} />
              </button>

              {sent ? (
                /* ═══════ SUCCESS SCREEN ═══════ */
                <div className="text-center py-6 space-y-4">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", damping: 12 }}
                    className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 grid place-items-center shadow-xl shadow-amber-500/40"
                  >
                    <CheckCircle2 size={40} className="text-black" />
                  </motion.div>
                  <h3 className="text-2xl font-extrabold text-slate-900 dark:text-white">{t.successTitle}</h3>
                  <p className="text-sm text-slate-600 dark:text-slate-300">{t.successBody}</p>
                  <div className="mx-auto inline-block px-4 py-2 rounded-xl bg-amber-50 dark:bg-amber-400/10 border border-amber-300/60 dark:border-amber-400/25">
                    <p className="text-[10px] text-slate-500 dark:text-slate-400">{t.successRef}</p>
                    <p className="text-lg font-black tracking-widest text-amber-700 dark:text-amber-300">{refId}</p>
                  </div>
                  <div>
                    <button onClick={resetForm} className="text-sm font-bold text-amber-700 dark:text-amber-300 underline">
                      {t.sendAnother}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {/* HEADER */}
                  <div className="text-center mb-4">
                    <p className="text-[9px] font-black tracking-[2px] text-amber-700 dark:text-amber-300">ੴ · {t.badge}</p>
                    <h3 className="mt-1">
                      <RotatingTitle titles={t.titles} />
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{t.subtitle}</p>
                  </div>

                  {/* LANGUAGE PILLS */}
                  <div className="grid grid-cols-3 gap-1 rounded-2xl bg-slate-100 dark:bg-white/[0.06] p-1 mb-4">
                    {LANGS.map((l) => (
                      <button
                        key={l.code}
                        onClick={() => setLang(l.code)}
                        className={cn(
                          "py-2 rounded-xl text-xs font-extrabold transition-all",
                          lang === l.code
                            ? "bg-gradient-to-r from-amber-400 to-amber-600 text-slate-950 shadow"
                            : "text-slate-600 dark:text-slate-300"
                        )}
                      >
                        {l.label}
                      </button>
                    ))}
                  </div>

                  {/* REF ID */}
                  <div className="mb-3 flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-50 dark:bg-amber-400/10 border border-amber-300/60 dark:border-amber-400/25">
                    <FileText size={14} className="text-amber-700 dark:text-amber-300 shrink-0" />
                    <span className="text-[10px] text-slate-500 dark:text-slate-400">{t.refLabel}</span>
                    <span className="ml-auto text-sm font-black tracking-widest text-amber-700 dark:text-amber-300">{refId}</span>
                  </div>

                  {/* NAME */}
                  <label className={labelCls}>{t.nameLabel}</label>
                  <div className="relative mb-3">
                    <User size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                    <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t.namePh} className={cn(inputCls, "pl-9")} />
                  </div>

                  {/* CONTACT */}
                  <label className={labelCls}>{t.contactLabel}</label>
                  <div className="relative mb-2">
                    <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                    <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t.emailPh} className={cn(inputCls, "pl-9")} />
                  </div>
                  <p className="text-center text-[10px] font-black text-slate-400 dark:text-slate-500 my-1">— {t.or} —</p>
                  <div className="relative mb-3">
                    <Phone size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                    <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={t.phonePh} className={cn(inputCls, "pl-9")} />
                  </div>

                  {/* MESSAGE */}
                  <label className={labelCls}>{t.msgLabel}</label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={t.msgPh}
                    rows={4}
                    className={cn(inputCls, "resize-none mb-2")}
                  />

                  {error && (
                    <p className="text-xs font-bold text-red-600 dark:text-red-400 mb-2">{error}</p>
                  )}

                  <button
                    onClick={submit}
                    disabled={sending}
                    className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-amber-400 to-amber-600 text-slate-950 font-extrabold shadow-lg shadow-amber-500/30 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <Send size={17} />
                    {sending ? t.sending : t.submit}
                  </button>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>,
      document.body
    )
  );
}